/**
 * @file Select 自定义下拉选择
 * @description 非原生 select；列表 Portal + 视口边界/maxHeight + fade；列表内 ScrollArea。
 *
 * @whenToUse 表单单选枚举、筛选条件下拉。
 * @whenNotToUse 可搜索远程选项（用 Combobox）；原生系统选择器场景。
 */

import {
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/cn.ts'
import {
  computeDropdownBox,
  floatingPanelStyle,
  type FloatingBox,
} from '@/lib/floating.ts'
import { ScrollArea } from '@/components/ui/scroll-area.tsx'
import {
  searchControlWidth,
  useSearchForm,
} from '@/components/ui/search-form-context.tsx'

export type SelectOption = {
  /** 选项值 */
  value: string
  /** 展示文案 */
  label: ReactNode
  disabled?: boolean
}

export type SelectProps = {
  /** 选项列表 */
  options: SelectOption[]
  /** 当前值 */
  value?: string
  /** 值变化 */
  onChange?: (value: string) => void
  /** 占位 */
  placeholder?: string
  /** 禁用 */
  disabled?: boolean
  /** 校验失败 */
  invalid?: boolean
  /** 触发器 class */
  className?: string
  /** id（配合 Label） */
  id?: string
}

export function Select({
  options,
  value,
  onChange,
  placeholder = '请选择',
  disabled = false,
  invalid = false,
  className,
  id,
}: SelectProps) {
  const inSearchForm = useSearchForm()
  const listId = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const [box, setBox] = useState<FloatingBox | null>(null)

  const selected = options.find((item) => item.value === value)

  /** 与选项行 h-8、列表容器 p-1 对齐；空选项按单行「暂无数据」计 */
  const OPTION_ROW_HEIGHT = 32
  const LIST_PADDING = 8
  const listContentHeight =
    Math.max(options.length, 1) * OPTION_ROW_HEIGHT + LIST_PADDING
  const maxListHeight = Math.min(box?.maxHeight ?? listContentHeight, 300)
  const needsVerticalScroll = listContentHeight > maxListHeight
  const measuredWidth = measureRef.current?.offsetWidth ?? 0
  const needsHorizontalScroll = measuredWidth > (box?.width ?? 0) + 1
  const needsScroll = needsVerticalScroll || needsHorizontalScroll

  const scrollOrientation = needsVerticalScroll && needsHorizontalScroll
    ? 'both'
    : needsHorizontalScroll
      ? 'horizontal'
      : 'vertical'

  const listContent =
    options.length === 0 ? (
      <div className="flex h-8 items-center justify-center px-2 text-sm text-muted">
        暂无数据
      </div>
    ) : (
      <div className={cn('p-1', needsHorizontalScroll && 'w-max min-w-full')}>
      {options.map((option) => {
        const isActive = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="option"
            aria-selected={isActive}
            disabled={option.disabled}
            className={cn(
              'flex h-8 w-full items-center justify-between gap-2 whitespace-nowrap rounded-md px-2 text-left text-sm transition-opacity duration-200 hover:bg-muted/20 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50',
              needsHorizontalScroll && 'min-w-max',
              isActive ? 'text-primary' : 'text-foreground',
            )}
            onClick={() => {
              if (option.disabled) return
              onChange?.(option.value)
              setOpen(false)
            }}
          >
            <span>{option.label}</span>
            {isActive ? (
              <Check className="size-3.5 shrink-0" aria-hidden />
            ) : (
              <span className="size-3.5 shrink-0" aria-hidden />
            )}
          </button>
        )
      })}
      </div>
    )

  const updateBox = () => {
    const el = triggerRef.current
    if (!el) return
    const contentWidth = measureRef.current?.offsetWidth ?? el.offsetWidth
    setBox(computeDropdownBox(el.getBoundingClientRect(), 4, undefined, {
      matchTriggerWidth: false,
      contentWidth,
    }))
  }

  useEffect(() => {
    if (open) {
      setMounted(true)
      const frameId = requestAnimationFrame(() => {
        updateBox()
        requestAnimationFrame(() => setVisible(true))
      })
      return () => cancelAnimationFrame(frameId)
    }
    setVisible(false)
    const timer = window.setTimeout(() => setMounted(false), 200)
    return () => window.clearTimeout(timer)
  }, [open, options])

  useEffect(() => {
    if (!open) return
    const onWin = () => updateBox()
    window.addEventListener('resize', onWin)
    window.addEventListener('scroll', onWin, true)
    return () => {
      window.removeEventListener('resize', onWin)
      window.removeEventListener('scroll', onWin, true)
    }
  }, [open, options])

  useEffect(() => {
    if (!open) return
    const onPointer = (event: MouseEvent) => {
      const t = event.target as Node
      if (triggerRef.current?.contains(t)) return
      if (panelRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <>
      <div
        ref={measureRef}
        aria-hidden
        className="pointer-events-none fixed left-[-9999px] top-0 w-max rounded-lg border border-border opacity-0"
      >
        <div className="p-1">
          {options.map((option) => (
            <div
              key={option.value}
              className="flex h-8 items-center gap-2 whitespace-nowrap px-2 text-sm"
            >
              <span>{option.label}</span>
              <Check className="size-3.5 shrink-0 opacity-0" aria-hidden />
            </div>
          ))}
        </div>
      </div>

      <button
        id={id}
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        className={cn(
          'inline-flex h-8 items-center justify-between gap-1.5 rounded-lg border bg-raised px-2 text-left text-sm transition-opacity duration-200 disabled:cursor-not-allowed disabled:opacity-50',
          searchControlWidth(inSearchForm),
          invalid ? 'border-danger' : 'border-border',
          className,
        )}
        onClick={() => setOpen((v) => !v)}
      >
        <span
          className={cn(
            'min-w-0 flex-1 truncate',
            selected ? 'text-foreground' : 'text-muted',
          )}
        >
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          className={cn(
            'size-4 shrink-0 text-muted transition-transform duration-200',
            open && 'rotate-180',
          )}
          aria-hidden
        />
      </button>

      {mounted && box && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={panelRef}
              id={listId}
              role="listbox"
              className={cn(
                'fixed z-50 overflow-hidden rounded-lg border border-border bg-sidebar/85 backdrop-blur-sm transition-opacity duration-200',
                visible ? 'opacity-100' : 'opacity-0',
              )}
              style={{
                ...floatingPanelStyle(box),
                maxHeight: maxListHeight,
                ...(needsVerticalScroll ? { height: maxListHeight } : {}),
              }}
            >
              {needsScroll ? (
                <ScrollArea
                  className={needsVerticalScroll ? 'h-full w-full' : 'w-full'}
                  orientation={scrollOrientation}
                >
                  {listContent}
                </ScrollArea>
              ) : (
                listContent
              )}
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
